import {
  collection,
  getDocs,
  query,
  limit,
  startAfter,
  QueryDocumentSnapshot,
  DocumentData,
  orderBy,
  getCountFromServer,
  where,
} from 'firebase/firestore';
import Failure from '../../../core/Failure';
import { FirestoreCollections, firestore } from '../../../firebase.config';

import Subscription from '../../models/Subscription';
import AI from '../../models/AI';
import { getAIsBatch } from '../../../utils/AIsUtils';

async function getSubscriptions(
  offset: number,
  lastVisible?: QueryDocumentSnapshot<DocumentData, DocumentData>,
  filterWithSubscriptionIds?: string[]
): Promise<{
  subscriptions: Subscription[];
  lastVisible: QueryDocumentSnapshot<DocumentData, DocumentData>;
  totalCollectionCount: number;
  ais: Map<string, AI>;
}> {
  try {
    const db = firestore;
    const subscriptionsCollection = collection(
      db,
      FirestoreCollections.subscriptions
    );

    const results = lastVisible
      ? await getDocs(
          filterWithSubscriptionIds
            ? query(
                subscriptionsCollection,
                where('id', 'in', filterWithSubscriptionIds),
                orderBy('last_activity', 'desc'),
                startAfter(lastVisible),
                limit(offset)
              )
            : query(
                subscriptionsCollection,
                orderBy('created', 'desc'),
                startAfter(lastVisible),
                limit(offset)
              )
        )
      : filterWithSubscriptionIds
      ? await getDocs(
          query(
            subscriptionsCollection,
            where('id', 'in', filterWithSubscriptionIds),
            orderBy('last_activity', 'desc'),
            limit(offset)
          )
        )
      : await getDocs(
          query(
            subscriptionsCollection,
            orderBy('created', 'desc'),
            limit(offset)
          )
        );

    const subscriptions: Subscription[] = [];

    results.docs.forEach((doc) => {
      subscriptions.push(Subscription.fromFirestore(doc));
    });

    // Fetch AIs in batch for all subscriptions
    const aiIds = subscriptions
      .map(sub => sub.idAi)
      .filter(idAi => idAi && idAi.trim() !== '');
    const ais = await getAIsBatch(aiIds);

    // Create a Map subscriptionId -> AI for easier access
    const subscriptionAIs = new Map<string, AI>();
    subscriptions.forEach(sub => {
      if (sub.idAi && ais.has(sub.idAi)) {
        const ai = ais.get(sub.idAi);
        if (ai) {
          subscriptionAIs.set(sub.id, ai);
        }
      }
    });

    const subscriptionsSnapshot = filterWithSubscriptionIds
      ? await getCountFromServer(
          query(
            subscriptionsCollection,
            where('id', 'in', filterWithSubscriptionIds)
          )
        )
      : await getCountFromServer(subscriptionsCollection);

    return {
      subscriptions,
      lastVisible: results.docs[results.docs.length - 1] ?? {},
      totalCollectionCount: subscriptionsSnapshot.data().count ?? 0,
      ais: subscriptionAIs
    };
  } catch (e) {
    console.log('error: ', e);
    throw new GetSubscriptionsFailure();
  }
}

export class GetSubscriptionsFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}

export default getSubscriptions;
